// Exports: MainTableView
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../../screens/Project Dashboard/Tasks/maintable.css";
import TaskEmptyIcon from "assets/empty state/task.svg";
import { useAlert } from "components/UI_components/Alert";
import FilterBar from "./FilterBar";
import GroupBlock from "./GroupBlock";
import useTaskApi from "hooks/useTaskApi";
import { getTaskDisplayStatus, priorityToNumeric } from "utils/tasks/taskHelpers";

const MainTableView = ({
  onRefresh,
  refreshing,
  taskGroups,
  refetchTasks,
  refetchSubtasks,
  mergeGroupAllocatedMembers,
  onAddTaskGroupClick,
  onAddTaskClick,
  onAddSubtaskClick,
  handleAddTask,
  showAddTaskModal,
  setShowAddTaskModal,
  showAddSubtaskModal,
  setShowAddSubtaskModal,
  handleAddSubtask,
  onToggleGroup,
  onToggleTask,
  onTaskReorder,
  onSubtaskReorder,
  onToggleSubComplete,
  onTaskStatusChange,
  onTaskPriorityChange,
  statusOptions,
  openStatusDropdown,
  setOpenStatusDropdown,
  openPriorityDropdown,
  setOpenPriorityDropdown,
  getInitials,
  getStatusProportions,
  editingGroupId,
  editingGroupName,
  onGroupEditStart,
  onGroupEditCancel,
  onGroupEditSubmit,
  editingTaskId,
  onTaskEditStart,
  onTaskEditCancel,
  onTaskEditSubmit,
  handleTaskDateChange,
  handleSubtaskDateChange,
  editingSubId,
  onSubEditStart,
  onSubEditCancel,
  onSubEditSubmit,
  setSelectedTaskGroup,
  setSelectedTask,
  setSelectedSubtask,
  showAddTaskGroupInput,
  setShowAddTaskGroupInput,
  addTaskGroup,
  preserveScroll,
  hasGroups,
}) => {
  const { addAlert } = useAlert();
  const { getSiteMembers, updateTask, addTaskMembers } = useTaskApi();
  const [siteMembers, setSiteMembers] = useState([]);
  const [currentUserMember, setCurrentUserMember] = useState(null);
  const [taskAuthority, setTaskAuthority] = useState(false);
  const [newTaskGroupName, setNewTaskGroupName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [bulkEditGroupId, setBulkEditGroupId] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [bulkMembers, setBulkMembers] = useState([]);
  const [bulkPriority, setBulkPriority] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showBulkAssigneeDropdown, setShowBulkAssigneeDropdown] =
    useState(false);

  const newTaskGroupInputRef = useRef(null);
  const isSubmittingGroupRef = useRef(false);
  const isOwner = localStorage.getItem("isOwner") === "true";

  useEffect(() => {
    if (!showAddTaskGroupInput || !newTaskGroupInputRef.current) return;

    const timer = window.setTimeout(() => {
      newTaskGroupInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
      const input = newTaskGroupInputRef.current.querySelector("input");
      input?.focus();
    }, 100);

    return () => window.clearTimeout(timer);
  }, [showAddTaskGroupInput]);

  useEffect(() => {
    let isMounted = true;
    const siteId = localStorage.getItem("site");

    if (!siteId) return undefined;

    const fetchMembers = async () => {
      try {
        const response = await getSiteMembers(siteId);
        if (!isMounted || !response?.status) return;

        const members = response.data || [];
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const currentMember = members.find(
          (member) => member.user_id === user.user
        );

        setSiteMembers(members);
        setCurrentUserMember(currentMember || null);
        setTaskAuthority(
          Boolean(
            currentMember?.create_taskgroup ||
              currentMember?.is_admin ||
              String(currentMember?.joined_as || "").toLowerCase() === "owner"
          )
        );
      } catch (error) {
        console.error("Error fetching site members:", error);
      }
    };

    fetchMembers();

    return () => {
      isMounted = false;
    };
  }, [getSiteMembers]);

  const closeAddForms = useCallback(() => {
    setShowAddTaskModal(null);
    setShowAddSubtaskModal(null);
  }, [setShowAddTaskModal, setShowAddSubtaskModal]);

  const resetBulkState = useCallback(() => {
    setSelectedTaskIds([]);
    setBulkMembers([]);
    setBulkPriority("");
    setShowBulkAssigneeDropdown(false);
  }, []);

  const toggleBulkMemberSelection = useCallback((memberId) => {
    setBulkMembers((current) =>
      current.includes(memberId)
        ? current.filter((id) => id !== memberId)
        : [...current, memberId]
    );
  }, []);

  const handleBulkSave = useCallback(
    async (group) => {
      if (!selectedTaskIds.length) {
        addAlert("Select at least one task.", "warning");
        return;
      }

      if (!bulkMembers.length && !bulkPriority) {
        addAlert("Choose at least one bulk action.", "warning");
        return;
      }

      try {
        setBulkSaving(true);

        const operations = [];

        for (const taskId of selectedTaskIds) {
          if (bulkPriority) {
            operations.push(
              updateTask(
                taskId,
                { priority: priorityToNumeric[bulkPriority] },
                { shouldAlert: false }
              )
            );
          }

          if (bulkMembers.length) {
            const task = group.tasks.find((candidate) => candidate.id === taskId);
            const existingMemberIds = (task?.members || []).map(
              (member) => member.member_id
            );
            const memberIdsToAdd = bulkMembers.filter(
              (memberId) => !existingMemberIds.includes(memberId)
            );

            if (memberIdsToAdd.length) {
              operations.push(
                addTaskMembers(
                  {
                    task_id: taskId,
                    member_ids: memberIdsToAdd,
                  },
                  { shouldAlert: false }
                )
              );
            }
          }
        }

        await Promise.allSettled(operations);
        await refetchTasks(group.id);
        addAlert("Bulk changes applied.", "success");
      } catch (error) {
        console.error("Bulk action failed:", error);
        addAlert("Failed to apply bulk changes.", "error");
      } finally {
        setBulkSaving(false);
        setBulkEditGroupId(null);
        resetBulkState();
      }
    },
    [
      addAlert,
      addTaskMembers,
      bulkMembers,
      bulkPriority,
      refetchTasks,
      resetBulkState,
      selectedTaskIds,
      updateTask,
    ]
  );

  const matchesFilters = useCallback(
    (taskLike) => {
      const searchMatches =
        searchTerm === "" ||
        taskLike?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const priorityMatches =
        priorityFilter === "all" || taskLike?.priority === priorityFilter;
      const statusMatches =
        statusFilter === "all" ||
        getTaskDisplayStatus(taskLike) === statusFilter ||
        taskLike?.status === statusFilter;

      return searchMatches && priorityMatches && statusMatches;
    },
    [priorityFilter, searchTerm, statusFilter]
  );

  const filteredGroups = useMemo(() => {
    if (!searchTerm && statusFilter === "all" && priorityFilter === "all") {
      return taskGroups;
    }

    return taskGroups.map((group) => ({
      ...group,
      tasks: group.tasks.filter((task) => {
        const taskMatches = matchesFilters(task);
        const hasMatchingSubtasks =
          task.subtasks?.some((subtask) => matchesFilters(subtask)) || false;
        return taskMatches || hasMatchingSubtasks;
      }),
    }));
  }, [matchesFilters, priorityFilter, searchTerm, statusFilter, taskGroups]);

  const filteredTaskCount = useMemo(
    () =>
      filteredGroups.reduce((runningTotal, group) => runningTotal + group.tasks.length, 0),
    [filteredGroups]
  );
  const isFiltering =
    Boolean(searchTerm) ||
    statusFilter !== "all" ||
    priorityFilter !== "all";

  const handleNewTaskGroupSubmit = async (event) => {
    event.preventDefault();
    if (!newTaskGroupName.trim() || isSubmittingGroupRef.current) return;

    isSubmittingGroupRef.current = true;
    await preserveScroll(async () => {
      await addTaskGroup({ name: newTaskGroupName.trim() });
      setNewTaskGroupName("");
      setShowAddTaskGroupInput(false);
    });
    isSubmittingGroupRef.current = false;
  };

  const renderAddTaskGroupInput = (wrapperClassName = "px-4 py-2 mb-4 scroll-mt-24") => (
    <div ref={newTaskGroupInputRef} className={wrapperClassName}>
      <div
        className="flex overflow-visible rounded-lg bg-white border border-gray-300 border-l-4"
        style={{ borderLeftColor: "#3B0069" }}
      >
        <div className="flex-grow px-4 py-2">
          <form
            onSubmit={handleNewTaskGroupSubmit}
            className="flex items-center w-full"
          >
            <input
              autoFocus
              type="text"
              placeholder="Enter Task Group Name..."
              className="flex-grow text-sm xl:text-[18px] font-normal border-b-2 border-purple-300 focus:border-purple-600 focus:outline-none px-2 py-1 mr-4 bg-transparent"
              value={newTaskGroupName}
              onChange={(event) => setNewTaskGroupName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setShowAddTaskGroupInput(false);
                  setNewTaskGroupName("");
                }
              }}
            />
            <div className="flex items-center space-x-2">
              <button
                type="submit"
                className="px-4 py-1.5 bg-[#3B0069] text-white rounded-md text-sm hover:bg-purple-800 transition-colors"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddTaskGroupInput(false);
                  setNewTaskGroupName("");
                }}
                className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        onRefresh={() => preserveScroll(() => onRefresh())}
        refreshing={refreshing}
        onAddTaskGroupClick={onAddTaskGroupClick}
        canAddTaskGroup={isOwner || taskAuthority}
      />

      {showAddTaskGroupInput && !hasGroups && renderAddTaskGroupInput()}

      <div className="px-4 py-1">
        {(searchTerm || statusFilter !== "all" || priorityFilter !== "all") && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="text-sm text-blue-800">
                {searchTerm && `Searching for "${searchTerm}" `}
                {statusFilter !== "all" && `Status: ${statusFilter} `}
                {priorityFilter !== "all" && `Priority: ${priorityFilter}`}
                {` • ${filteredTaskCount} tasks found`}
              </div>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setPriorityFilter("all");
                }}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {filteredGroups.length > 0 ? (
          filteredGroups.map((group) => (
            <GroupBlock
              key={group.id}
              tg={group}
              siteMembers={siteMembers}
              taskAuthority={taskAuthority}
              currentUserMember={currentUserMember}
              editingGroupId={editingGroupId}
              editingGroupName={editingGroupName}
              onGroupEditStart={onGroupEditStart}
              onGroupEditCancel={onGroupEditCancel}
              onGroupEditSubmit={onGroupEditSubmit}
              bulkEditGroupId={bulkEditGroupId}
              setBulkEditGroupId={setBulkEditGroupId}
              selectedTaskIds={selectedTaskIds}
              setSelectedTaskIds={setSelectedTaskIds}
              bulkMembers={bulkMembers}
              bulkPriority={bulkPriority}
              setBulkPriority={setBulkPriority}
              bulkSaving={bulkSaving}
              onBulkSave={handleBulkSave}
              onBulkReset={resetBulkState}
              toggleBulkMemberSelection={toggleBulkMemberSelection}
              showBulkAssigneeDropdown={showBulkAssigneeDropdown}
              setShowBulkAssigneeDropdown={setShowBulkAssigneeDropdown}
              onToggleGroup={onToggleGroup}
              onToggleTask={onToggleTask}
              onTaskReorder={onTaskReorder}
              onSubtaskReorder={onSubtaskReorder}
              taskReorderDisabled={isFiltering}
              onAddTaskClick={onAddTaskClick}
              showAddTaskModal={showAddTaskModal}
              setShowAddTaskModal={setShowAddTaskModal}
              showAddSubtaskModal={showAddSubtaskModal}
              setShowAddSubtaskModal={setShowAddSubtaskModal}
              handleAddTask={handleAddTask}
              handleAddSubtask={handleAddSubtask}
              onAddSubtaskClick={onAddSubtaskClick}
              setSelectedTaskGroup={setSelectedTaskGroup}
              setSelectedTask={setSelectedTask}
              setSelectedSubtask={setSelectedSubtask}
              getInitials={getInitials}
              preserveScroll={preserveScroll}
              closeAddForms={closeAddForms}
              editingTaskId={editingTaskId}
              onTaskEditStart={onTaskEditStart}
              onTaskEditCancel={onTaskEditCancel}
              onTaskEditSubmit={onTaskEditSubmit}
              editingSubId={editingSubId}
              onSubEditStart={onSubEditStart}
              onSubEditCancel={onSubEditCancel}
              onSubEditSubmit={onSubEditSubmit}
              handleTaskDateChange={handleTaskDateChange}
              handleSubtaskDateChange={handleSubtaskDateChange}
              onToggleSubComplete={onToggleSubComplete}
              onTaskStatusChange={onTaskStatusChange}
              onTaskPriorityChange={onTaskPriorityChange}
              openStatusDropdown={openStatusDropdown}
              setOpenStatusDropdown={setOpenStatusDropdown}
              openPriorityDropdown={openPriorityDropdown}
              setOpenPriorityDropdown={setOpenPriorityDropdown}
              statusOptions={statusOptions}
              refetchTasks={refetchTasks}
              refetchSubtasks={refetchSubtasks}
              mergeGroupAllocatedMembers={mergeGroupAllocatedMembers}
              getStatusProportions={getStatusProportions}
            />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-10">
            <img src={TaskEmptyIcon} alt="No tasks" className="mb-4" width={300} />
            <span className="text-gray-500 text-2xl">
              {searchTerm || statusFilter !== "all" || priorityFilter !== "all"
                ? "No tasks match your filters"
                : "No Task Groups"}
            </span>
            {(searchTerm || statusFilter !== "all" || priorityFilter !== "all") && (
              <span className="text-gray-500 text-sm mt-2">
                Try adjusting your search or filter criteria
              </span>
            )}
            {!searchTerm && statusFilter === "all" && priorityFilter === "all" && (
              <span className="text-gray-500 text-sm">
                Add a Task Group to organize your tasks
              </span>
            )}
          </div>
        )}
      </div>

      {showAddTaskGroupInput && hasGroups && renderAddTaskGroupInput("px-4 py-2 mb-20 scroll-mt-24")}
    </>
  );
};

export default MainTableView;
